import React, { useMemo, useState } from "react";
import { withTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  CircularProgress,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import PropTypes from "prop-types";

import Modal from "Components/Common/Modal";
import Conversation from "Components/Common/Conversation";
import { getOptionLabel } from "common/enums";

import {
  useComplaintConversationQuery,
  usePostComplaintConversationMutation,
} from "apis/ConsumerComplaintConversation";
import { READ_ADMIN_ROLES } from "common/constant";

const ComplaintConversation = ({
  isOpen,
  handleClose,
  complaintId,
  complaintStatus,
  t,
}) => {
  const { id } = useSelector((state) => state.Login.user);
  const [response, setResponse] = useState("");

  const { roles } = useSelector((state) => state.Login);
  const isReadAdmin = roles?.some((role) => READ_ADMIN_ROLES.includes(role));

  const { data: { data: consumerConversationData } = [] } =
    useComplaintConversationQuery(complaintId);
  const [addConsumerResponse, { isLoading }] =
    usePostComplaintConversationMutation();

  const responseSubmitHandler = async () => {
    try {
      await addConsumerResponse({ consumerComplaintId: complaintId, response });
      setResponse("");
    } catch (error) {}
  };

  const handleKeyDown = (e) => {
    if (e.code === "Enter" && !e.shiftKey) {
      e.preventDefault();
      responseSubmitHandler();
    }
  };

  const responseMessage = useMemo(
    () =>
      consumerConversationData
        ? consumerConversationData.map(
            ({ userId, response, commentDateTime, userName }) =>
              userId === id
                ? { clientMessage: response, userName, commentDateTime }
                : { adminMessage: response, userName, commentDateTime }
          )
        : [],
    [consumerConversationData]
  );

  return (
    <Modal
      isOpen={isOpen}
      handleClose={handleClose}
      title="Complaint Discussion"
      maxWidth="sm"
    >
      <>
        <Conversation conversationResponses={responseMessage} />
        {complaintStatus !== getOptionLabel("complaintStatus", 3) &&
          !isReadAdmin && (
            <Box className="mt-4">
              <TextField
                placeholder={t("Please enter your response")}
                className="mb-1"
                multiline
                minRows={1}
                maxRows={2}
                disabled={isLoading}
                onKeyDown={handleKeyDown}
                value={response}
                onChange={(e) => {
                  setResponse(e.target.value);
                }}
                fullWidth
                size="small"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        className="cursor-pointer"
                        onClick={responseSubmitHandler}
                        edge="end"
                        disabled={!response || isLoading}
                      >
                        {isLoading ? (
                          <CircularProgress
                            sx={{ marginLeft: "5px" }}
                            size={20}
                          />
                        ) : (
                          <SendIcon
                            color={
                              !response || isLoading ? "disabled" : "primary"
                            }
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
      </>
    </Modal>
  );
};

ComplaintConversation.propTypes = {
  isOpen: PropTypes.bool,
  handleClose: PropTypes.func,
  complaintId: PropTypes.number,
  complaintStatus: PropTypes.string,
  t: PropTypes.func,
};

export default withTranslation()(ComplaintConversation);
